import { io, Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  GameStateSnapshot,
  QueueState,
  Direction,
  PlayerIdentity,
} from '@twitch-snake/common';
import { GameRenderer } from './renderer';
import { generateGuestIdentity } from './guestNames';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Production path prefix
const API_BASE = window.location.pathname.startsWith('/streamsnake') ? '/streamsnake' : '';

// DOM Elements
const loginBtn = document.getElementById('login-btn') as HTMLButtonElement;
const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;
const userName = document.getElementById('user-name') as HTMLSpanElement;
const joinQueueBtn = document.getElementById('join-queue-btn') as HTMLButtonElement;
const leaveQueueBtn = document.getElementById('leave-queue-btn') as HTMLButtonElement;
const queuePosition = document.getElementById('queue-position') as HTMLElement;
const queuePositionSpan = queuePosition.querySelector('span') as HTMLSpanElement;
const gamePhase = document.getElementById('game-phase') as HTMLSpanElement;
const queueCount = document.getElementById('queue-count') as HTMLSpanElement;
const currentPlayerInfo = document.getElementById('current-player-info') as HTMLElement;
const currentPlayerName = document.getElementById('current-player-name') as HTMLElement;
const queueList = document.getElementById('queue-list') as HTMLUListElement;
const calledBanner = document.getElementById('called-banner') as HTMLElement;
const acceptTimer = document.getElementById('accept-timer') as HTMLSpanElement;
const scoreDisplay = document.getElementById('score') as HTMLSpanElement;
const lengthDisplay = document.getElementById('length') as HTMLSpanElement;
const turnTimer = document.getElementById('turn-timer') as HTMLSpanElement;
const connectionIndicator = document.getElementById('connection-indicator') as HTMLSpanElement;
const connectionText = document.getElementById('connection-text') as HTMLSpanElement;
const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const leaderboardList = document.getElementById('leaderboard-list') as HTMLOListElement;
const leaderboardTabs = document.getElementById('leaderboard-tabs') as HTMLElement;
const turnEndPopup = document.getElementById('turn-end-popup') as HTMLElement;
const popupScore = document.getElementById('popup-score') as HTMLSpanElement;
const popupCloseBtn = document.getElementById('popup-close') as HTMLButtonElement;
const demoOverlay = document.getElementById('demo-overlay') as HTMLElement;
const playNowBtn = document.getElementById('play-now-btn') as HTMLButtonElement;
const loginPrompt = document.getElementById('login-prompt') as HTMLElement;
const loginPromptBtn = document.getElementById('login-prompt-btn') as HTMLButtonElement;
const guestPlayBtn = document.getElementById('guest-play-btn') as HTMLButtonElement;
const batchWaitBanner = document.getElementById('batch-wait-banner') as HTMLElement;
const batchWaitTimer = document.getElementById('batch-wait-timer') as HTMLSpanElement;
const batchPlayersCount = document.getElementById('batch-players-count') as HTMLSpanElement;
const batchJoinBtn = document.getElementById('batch-join-btn') as HTMLButtonElement;
const twitchChatIndicator = document.getElementById('twitch-chat-indicator') as HTMLSpanElement;
const twitchChatText = document.getElementById('twitch-chat-text') as HTMLSpanElement;
const personalBest = document.getElementById('personal-best') as HTMLElement;
const personalBestScore = document.getElementById('personal-best-score') as HTMLSpanElement;
const midgameJoinBanner = document.getElementById('midgame-join-banner') as HTMLElement;
const joinThisGameBtn = document.getElementById('join-this-game-btn') as HTMLButtonElement;
const joinNextGameBtn = document.getElementById('join-next-game-btn') as HTMLButtonElement;

// State
let currentUser: PlayerIdentity | null = null;
let myBestScore = 0;
let socket: TypedSocket;
let renderer: GameRenderer;
let acceptDeadline: number | null = null;
let turnDeadline: number | null = null;
let batchWaitDeadline: number | null = null;
let canJoinMidGame = false;
let pendingJoinMode: 'queue' | 'active' = 'queue';
let _timerInterval: ReturnType<typeof setInterval> | null = null;
void _timerInterval; // Reserved for future timer cleanup

// Konami code sequence
const KONAMI_SEQUENCE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a', 'Enter'];
let konamiIndex = 0;

// Initialize
async function init() {
  // Check if user is logged in
  await checkAuth();

  // Setup renderer
  renderer = new GameRenderer(canvas);

  // Connect to WebSocket
  connectSocket();

  // Setup event listeners
  setupEventListeners();

  // Start timer update loop
  startTimerLoop();

  // Load initial leaderboard
  fetchLeaderboard('today');

  // Initialize Twitch chat embed
  initTwitchChatEmbed();
}

async function checkAuth() {
  try {
    const res = await fetch(`${API_BASE}/api/me`);
    if (res.ok) {
      const user = await res.json();
      currentUser = {
        twitchUserId: user.twitchUserId,
        login: user.login,
        displayName: user.displayName,
        profileImageUrl: user.profileImageUrl,
      };
      updateAuthUI(true);
      fetchPersonalBest();
    } else {
      updateAuthUI(false);
    }
  } catch {
    updateAuthUI(false);
  }
}

function updateAuthUI(loggedIn: boolean) {
  if (loggedIn && currentUser) {
    userName.textContent = currentUser.displayName;
    loginBtn.style.display = 'none';
    logoutBtn.style.display = 'inline-block';
    personalBest.classList.remove('hidden');
  } else {
    userName.textContent = 'Not logged in';
    loginBtn.style.display = 'inline-block';
    logoutBtn.style.display = 'none';
    personalBest.classList.add('hidden');
  }
}

async function fetchPersonalBest() {
  try {
    const res = await fetch(`${API_BASE}/api/my-best`);
    if (res.ok) {
      const data = await res.json();
      myBestScore = data.bestScore ?? 0;
      personalBestScore.textContent = myBestScore.toLocaleString();
    }
  } catch { /* ignore */ }
}

function connectSocket() {
  const isProduction = window.location.pathname.startsWith('/streamsnake');
  socket = io({
    path: isProduction ? '/streamsnake/socket.io/' : '/socket.io/',
    transports: ['websocket'],
  });

  socket.on('connect', () => {
    updateConnectionStatus('connected');
  });

  socket.on('disconnect', () => {
    updateConnectionStatus('disconnected');
  });

  socket.on('queue_state', handleQueueState);
  socket.on('player_called', handlePlayerCalled);
  socket.on('turn_started', handleTurnStarted);
  socket.on('turn_ended', handleTurnEnded);
  socket.on('game_state_snapshot', handleGameState);
  socket.on('score_update', handleScoreUpdate);
  socket.on('leaderboard_update', handleLeaderboardUpdate);
  socket.on('system_notice', handleSystemNotice);
  socket.on('player_joined_midgame', handlePlayerJoinedMidgame);
  socket.on('player_revived', handlePlayerRevived);
  socket.on('player_respawning', handlePlayerRespawning);
}

function updateConnectionStatus(status: 'connected' | 'disconnected' | 'connecting') {
  connectionIndicator.className = status;
  connectionText.textContent = status.charAt(0).toUpperCase() + status.slice(1);
}

function handleQueueState(state: QueueState) {
  gamePhase.textContent = state.phase;
  queueCount.textContent = String(state.queue.length);

  // Show/hide demo overlay
  if (state.phase === 'demo' || state.phase === 'idle') {
    demoOverlay.classList.remove('hidden');
  } else {
    demoOverlay.classList.add('hidden');
  }

  // Update queue list
  queueList.innerHTML = '';
  state.queue.forEach((entry) => {
    const li = document.createElement('li');
    li.textContent = entry.player.displayName;
    if (state.currentPlayer?.twitchUserId === entry.player.twitchUserId) {
      li.classList.add('current');
    }
    queueList.appendChild(li);
  });

  // Update current player info
  if (state.currentPlayer) {
    currentPlayerInfo.style.display = 'block';
    currentPlayerName.textContent = state.currentPlayer.displayName;
  } else {
    currentPlayerInfo.style.display = 'none';
  }

  // Update user's queue position
  if (state.yourPosition && state.yourPosition > 0) {
    queuePosition.style.display = 'block';
    queuePositionSpan.textContent = String(state.yourPosition);
    joinQueueBtn.style.display = 'none';
    leaveQueueBtn.style.display = 'inline-block';
  } else {
    queuePosition.style.display = 'none';
    joinQueueBtn.style.display = 'inline-block';
    leaveQueueBtn.style.display = 'none';
  }

  // Update deadlines (adjust for server-client clock skew)
  const now = Date.now();
  const serverNow = state.serverTimeMs;
  acceptDeadline = state.acceptEndsAtMs ? now + (state.acceptEndsAtMs - serverNow) : null;
  turnDeadline = state.turnEndsAtMs ? now + (state.turnEndsAtMs - serverNow) : null;
  batchWaitDeadline = state.batchWaitEndsAtMs ? now + (state.batchWaitEndsAtMs - serverNow) : null;

  // Show/hide batch wait countdown banner - only when batch_wait phase AND players are waiting
  const queuedCount = state.queue.length;
  if (batchWaitDeadline && state.phase === 'batch_wait' && queuedCount > 0) {
    batchWaitBanner.classList.remove('hidden');
    batchPlayersCount.textContent = String(queuedCount);
    // Hide join button if already in queue
    const alreadyQueued = currentUser && state.queue.some(
      e => e.player.twitchUserId === currentUser!.twitchUserId
    );
    batchJoinBtn.style.display = alreadyQueued ? 'none' : 'inline-block';
  } else {
    batchWaitBanner.classList.add('hidden');
  }
}

function handlePlayerCalled(data: { player: PlayerIdentity; acceptDeadlineMs: number }) {
  // Check if it's the current user
  if (currentUser && data.player.twitchUserId === currentUser.twitchUserId) {
    calledBanner.style.display = 'block';
    acceptDeadline = data.acceptDeadlineMs;
    
    // Play notification sound (optional)
    playNotificationSound();
  }
}

function handleTurnStarted(data: { player: PlayerIdentity; players?: PlayerIdentity[]; turnEndsAtMs: number }) {
  calledBanner.style.display = 'none';
  turnDeadline = data.turnEndsAtMs;
  acceptDeadline = null;

  // Focus canvas if it's our turn (check if we're one of the players)
  const players = data.players || [data.player];
  if (currentUser && players.some(p => p.twitchUserId === currentUser!.twitchUserId)) {
    canvas.focus();
  }
}

function handleTurnEnded(data: { 
  result: { 
    player: PlayerIdentity;
    players?: PlayerIdentity[];
    stats: { score: number }; 
    playerResults?: Array<{ player: PlayerIdentity; stats: { score: number }; endReason?: string; dailyRank?: number; allTimeRank?: number; isTodayHighScore?: boolean; isAllTimeHighScore?: boolean }>;
    endReason: string;
    isTodayHighScore?: boolean;
    isAllTimeHighScore?: boolean;
  } 
}) {
  turnDeadline = null;
  
  // Check if we were one of the players
  const players = data.result.players || [data.result.player];
  if (!currentUser || !players.some(p => p.twitchUserId === currentUser!.twitchUserId)) {
    return;
  }
  
  // Find our result from playerResults, or use the main stats
  let ourScore = data.result.stats.score;
  let ourEndReason = data.result.endReason;
  let isTodayHigh = data.result.isTodayHighScore;
  let isAllTimeHigh = data.result.isAllTimeHighScore;
  let dailyRank: number | undefined;
  let allTimeRank: number | undefined;
  
  if (data.result.playerResults) {
    const ourResult = data.result.playerResults.find(
      r => r.player.twitchUserId === currentUser!.twitchUserId
    );
    if (ourResult) {
      ourScore = ourResult.stats.score;
      if (ourResult.endReason) {
        ourEndReason = ourResult.endReason;
      }
      dailyRank = ourResult.dailyRank;
      allTimeRank = ourResult.allTimeRank;
      isTodayHigh = ourResult.isTodayHighScore;
      isAllTimeHigh = ourResult.isAllTimeHighScore;
    }
  }
  
  showTurnEndPopup(ourScore, ourEndReason, isTodayHigh, isAllTimeHigh, dailyRank, allTimeRank);

  // Update personal best if this score is higher
  if (currentUser && !currentUser.isGuest && ourScore > myBestScore) {
    myBestScore = ourScore;
    personalBestScore.textContent = myBestScore.toLocaleString();
  }
}

const TURN_END_MESSAGES: Record<string, { title: string; subtitle: string }> = {
  timeout: { title: 'Time Up!', subtitle: "Good Job! You didn't die!" },
  wall_collision: { title: 'Game Over', subtitle: 'Collision' },
  self_collision: { title: 'Game Over', subtitle: 'Bit Yourself! Ouch! Better luck next time!' },
  ouroboros: { title: 'Game Over', subtitle: 'Ouroboros' },
  eaten: { title: 'Game Over', subtitle: 'Snake Food' },
  disconnect: { title: 'Disconnected', subtitle: 'Connection Lost' },
  skip: { title: 'Skipped', subtitle: 'Turn Passed' },
  admin: { title: 'Ended', subtitle: 'Admin Action' },
};

function showTurnEndPopup(
  score: number, 
  reason: string,
  _isTodayHighScore?: boolean,
  isAllTimeHighScore?: boolean,
  dailyRank?: number,
  allTimeRank?: number
) {
  const popupTitle = document.getElementById('popup-title')!;
  const popupSubtitle = document.getElementById('popup-subtitle')!;
  const popupHighscore = document.getElementById('popup-highscore')!;
  const messages = TURN_END_MESSAGES[reason] || { title: 'Turn Ended', subtitle: reason };
  
  popupTitle.textContent = messages.title;
  popupSubtitle.textContent = messages.subtitle;
  popupScore.textContent = String(score);

  // Show high score / leaderboard message
  // Prioritize all-time rank 1, then daily rank 1, then any leaderboard placement
  if (isAllTimeHighScore && allTimeRank === 1) {
    popupHighscore.textContent = '🏆 NEW ALL-TIME HIGH SCORE! 🏆';
    popupHighscore.classList.remove('hidden');
  } else if (typeof allTimeRank === 'number' && allTimeRank <= 20) {
    popupHighscore.textContent = `⭐ You made the Leaderboard! ⭐\n⭐ All-Time High Score #${allTimeRank} ⭐`;
    popupHighscore.classList.remove('hidden');
  } else if (typeof dailyRank === 'number' && dailyRank <= 20) {
    popupHighscore.textContent = `⭐ You made the Leaderboard! ⭐\n⭐ Daily High Score #${dailyRank} ⭐`;
    popupHighscore.classList.remove('hidden');
  } else {
    popupHighscore.classList.add('hidden');
  }

  turnEndPopup.classList.remove('hidden');
}

function hideTurnEndPopup() {
  turnEndPopup.classList.add('hidden');
}

function showLoginPrompt() {
  loginPrompt.classList.remove('hidden');
}

function hideLoginPrompt() {
  loginPrompt.classList.add('hidden');
}
void hideLoginPrompt; // Reserved for future use

function emitJoinQueue() {
  if (!currentUser) return;
  
  if (currentUser.isGuest) {
    // Pass guest identity to server
    socket.emit('join_queue', {
      guest: {
        twitchUserId: currentUser.twitchUserId,
        login: currentUser.login,
        displayName: currentUser.displayName,
        isGuest: true as const,
      }
    });
  } else {
    // Regular authenticated user
    socket.emit('join_queue');
  }
}

function handleGameState(state: GameStateSnapshot) {
  renderer.render(state);

  // Hide demo overlay during active game (defensive - queue_state should also handle this)
  if (state.phase === 'active_turn') {
    demoOverlay.classList.add('hidden');
  }

  // Update Twitch chat connection status
  if (state.twitchChatConnected !== undefined) {
    const chatConnected = state.twitchChatConnected;
    twitchChatIndicator.className = chatConnected ? 'connected' : 'disconnected';
    twitchChatText.textContent = chatConnected ? 'Chat: Connected' : 'Chat: Disconnected';
  }

  // Update mid-game join availability
  canJoinMidGame = state.canJoinMidGame ?? false;
  const isPlaying = currentUser && state.activePlayers?.some(
    p => p.twitchUserId === currentUser!.twitchUserId
  );
  if (canJoinMidGame && state.phase === 'active_turn' && !isPlaying) {
    midgameJoinBanner.classList.remove('hidden');
  } else {
    midgameJoinBanner.classList.add('hidden');
  }
}

function handleScoreUpdate(data: { playerId?: string; score: number; length: number }) {
  // Only update display if it's our score (or no playerId for backwards compat)
  if (!data.playerId || (currentUser && data.playerId === currentUser.twitchUserId)) {
    scoreDisplay.textContent = String(data.score);
    lengthDisplay.textContent = String(data.length);
  }
}

function handleLeaderboardUpdate(data: { today: Array<{ player: PlayerIdentity; score: number }>; allTime: Array<{ player: PlayerIdentity; score: number }> }) {
  // Update leaderboard based on active tab
  const activeTab = leaderboardTabs.querySelector('button.active')?.getAttribute('data-range') || 'today';
  const entries = activeTab === 'today' ? data.today : data.allTime;
  
  leaderboardList.innerHTML = '';
  entries.forEach((entry) => {
    const li = document.createElement('li');
    const nameSpan = document.createElement('span');
    nameSpan.textContent = entry.player.displayName;
    const scoreSpan = document.createElement('span');
    scoreSpan.className = 'score';
    scoreSpan.textContent = String(entry.score);
    li.appendChild(nameSpan);
    li.appendChild(scoreSpan);
    leaderboardList.appendChild(li);
  });
}

function handleSystemNotice(data: { message: string; type: 'info' | 'warning' | 'error' }) {
  console.log(`[${data.type}] ${data.message}`);
  // Could show a toast notification here
}

function handlePlayerJoinedMidgame(_data: { player: PlayerIdentity }) {
  // Hide the mid-game join banner if we just joined
  if (currentUser && _data.player.twitchUserId === currentUser.twitchUserId) {
    midgameJoinBanner.classList.add('hidden');
    canvas.focus();
  }
}

function handlePlayerRevived(data: { reviver: PlayerIdentity; revived: PlayerIdentity; reviverScore: number }) {
  console.log(`${data.reviver.displayName} revived ${data.revived.displayName}!`);
  // Show revive notification if it involves current user
  if (currentUser) {
    if (data.revived.twitchUserId === currentUser.twitchUserId) {
      console.log('You have been revived! Respawning in 5 seconds...');
    } else if (data.reviver.twitchUserId === currentUser.twitchUserId) {
      scoreDisplay.textContent = String(data.reviverScore);
    }
  }
}

function handlePlayerRespawning(data: { player: PlayerIdentity; respawnAtMs: number }) {
  if (currentUser && data.player.twitchUserId === currentUser.twitchUserId) {
    console.log(`Respawning at ${new Date(data.respawnAtMs).toISOString()}`);
  }
}

function emitJoinActiveGame() {
  if (!currentUser) return;

  if (currentUser.isGuest) {
    socket.emit('join_active_game', {
      guest: {
        twitchUserId: currentUser.twitchUserId,
        login: currentUser.login,
        displayName: currentUser.displayName,
        isGuest: true as const,
      }
    });
  } else {
    socket.emit('join_active_game');
  }
}

function setupEventListeners() {
  // Auth buttons
  loginBtn.addEventListener('click', () => {
    window.location.href = `${API_BASE}/auth/twitch/login`;
  });

  logoutBtn.addEventListener('click', async () => {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST' });
    window.location.reload();
  });

  // Queue buttons - handle both click and touch
  function handleJoinQueue() {
    if (!currentUser) {
      showLoginPrompt();
      return;
    }
    emitJoinQueue();
  }
  joinQueueBtn.addEventListener('click', handleJoinQueue);
  joinQueueBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleJoinQueue();
  }, { passive: false });

  // Batch wait "Join Game" button - same behavior as join queue
  batchJoinBtn.addEventListener('click', handleJoinQueue);
  batchJoinBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleJoinQueue();
  }, { passive: false });

  // Mid-game join buttons
  function handleJoinThisGame() {
    if (!currentUser) {
      pendingJoinMode = 'active';
      demoOverlay.classList.add('hidden');
      showLoginPrompt();
      return;
    }
    emitJoinActiveGame();
  }
  joinThisGameBtn.addEventListener('click', handleJoinThisGame);
  joinThisGameBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleJoinThisGame();
  }, { passive: false });

  function handleJoinNextGame() {
    if (!currentUser) {
      showLoginPrompt();
      return;
    }
    emitJoinQueue();
  }
  joinNextGameBtn.addEventListener('click', handleJoinNextGame);
  joinNextGameBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleJoinNextGame();
  }, { passive: false });

  function handleLeaveQueue() {
    socket.emit('leave_queue');
  }
  leaveQueueBtn.addEventListener('click', handleLeaveQueue);
  leaveQueueBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleLeaveQueue();
  }, { passive: false });

  // Login prompt button - handle both click and touch
  function handleLoginPrompt() {
    window.location.href = `${API_BASE}/auth/twitch/login`;
  }
  loginPromptBtn.addEventListener('click', handleLoginPrompt);
  loginPromptBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleLoginPrompt();
  }, { passive: false });

  // Guest play button - handle both click and touch
  function handleGuestPlay() {
    // Generate guest identity and set as current user
    currentUser = generateGuestIdentity();
    updateAuthUI(true);
    hideLoginPrompt();
    // Join based on what the user originally clicked
    if (pendingJoinMode === 'active') {
      emitJoinActiveGame();
    } else {
      emitJoinQueue();
    }
    pendingJoinMode = 'queue';
  }
  guestPlayBtn.addEventListener('click', handleGuestPlay);
  guestPlayBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleGuestPlay();
  }, { passive: false });

  // Play Now button (demo overlay) - handle both click and touch
  function handlePlayNow() {
    if (currentUser) {
      // User is logged in - join queue
      emitJoinQueue();
    } else {
      // Not logged in - show login prompt
      showLoginPrompt();
    }
  }
  
  playNowBtn.addEventListener('click', handlePlayNow);
  playNowBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handlePlayNow();
  }, { passive: false });

  // Keyboard controls
  canvas.addEventListener('keydown', handleKeyDown);
  canvas.tabIndex = 0; // Make canvas focusable

  // Mobile D-Pad controls
  setupMobileControls();

  // Accept turn on any input when called
  canvas.addEventListener('click', () => {
    if (calledBanner.style.display !== 'none') {
      socket.emit('accept_turn');
    }
    canvas.focus();
  });

  document.addEventListener('keydown', (_e) => {
    if (calledBanner.style.display !== 'none') {
      socket.emit('accept_turn');
    }

    // Konami code detection
    const key = _e.key;
    if (key === KONAMI_SEQUENCE[konamiIndex] || key.toLowerCase() === KONAMI_SEQUENCE[konamiIndex]?.toLowerCase()) {
      konamiIndex++;
      if (konamiIndex >= 2) {
        // We're mid-sequence — suppress arrow keys from being sent as movement
        _e.stopPropagation();
      }
      if (konamiIndex === KONAMI_SEQUENCE.length) {
        konamiIndex = 0;
        socket.emit('konami_code');
      }
    } else {
      konamiIndex = 0;
    }
  }, true); // useCapture = true so it fires before the canvas handler

  // Leaderboard tabs
  leaderboardTabs.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON') {
      leaderboardTabs.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      target.classList.add('active');
      fetchLeaderboard(target.getAttribute('data-range') || 'today');
    }
  });

  // Turn end popup close
  popupCloseBtn.addEventListener('click', hideTurnEndPopup);
  turnEndPopup.addEventListener('click', (e) => {
    if (e.target === turnEndPopup) hideTurnEndPopup();
  });
}

function handleKeyDown(e: KeyboardEvent) {
  const keyMap: Record<string, Direction> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    w: 'up',
    s: 'down',
    a: 'left',
    d: 'right',
  };

  const direction = keyMap[e.key];
  if (direction) {
    e.preventDefault();
    socket.emit('input_move', { direction });
  }

  if (e.key === ' ' || e.code === 'Space') {
    e.preventDefault();
    socket.emit('input_fire');
  }
}

function setupMobileControls() {
  const dpadBtns = document.querySelectorAll('.dpad-btn[data-direction]');
  const fireBtn = document.getElementById('fire-btn');
  const landscapePrompt = document.getElementById('landscape-prompt');
  
  // D-Pad direction buttons
  dpadBtns.forEach(btn => {
    const direction = btn.getAttribute('data-direction') as Direction;
    
    // Use touchstart for faster response
    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      socket.emit('input_move', { direction });
      
      // Also accept turn if waiting
      if (calledBanner.style.display !== 'none') {
        socket.emit('accept_turn');
      }
    }, { passive: false });
    
    // Fallback for click (mouse on touch devices)
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      socket.emit('input_move', { direction });
    });
  });
  
  // Fire button
  if (fireBtn) {
    fireBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      socket.emit('input_fire');
    }, { passive: false });
    
    fireBtn.addEventListener('click', (e) => {
      e.preventDefault();
      socket.emit('input_fire');
    });
  }
  
  // Landscape prompt - check orientation on load and resize
  function checkOrientation() {
    if (!landscapePrompt) return;
    
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    const isPortrait = window.innerHeight > window.innerWidth;
    
    if (isTouchDevice && isPortrait) {
      landscapePrompt.classList.remove('hidden');
    } else {
      landscapePrompt.classList.add('hidden');
    }
  }
  
  // Check orientation on load and when it changes
  checkOrientation();
  window.addEventListener('resize', checkOrientation);
  window.addEventListener('orientationchange', checkOrientation);
  
  // Prevent default touch behaviors on game area (no scroll/zoom)
  const gameArea = document.getElementById('game-area');
  if (gameArea) {
    gameArea.addEventListener('touchmove', (e) => {
      e.preventDefault();
    }, { passive: false });
  }
}

function startTimerLoop() {
  _timerInterval = setInterval(() => {
    const now = Date.now();

    if (acceptDeadline) {
      const remaining = Math.max(0, Math.ceil((acceptDeadline - now) / 1000));
      acceptTimer.textContent = String(remaining);
    }

    if (turnDeadline) {
      const remaining = Math.max(0, Math.ceil((turnDeadline - now) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      turnTimer.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    if (batchWaitDeadline) {
      const remaining = Math.max(0, Math.ceil((batchWaitDeadline - now) / 1000));
      batchWaitTimer.textContent = String(remaining);
    }
  }, 100);
}

async function fetchLeaderboard(range: string) {
  try {
    const res = await fetch(`${API_BASE}/api/leaderboard?range=${range}`);
    if (res.ok) {
      const entries = await res.json();
      leaderboardList.innerHTML = '';
      entries.forEach((entry: { player: PlayerIdentity; score: number }) => {
        const li = document.createElement('li');
        const nameSpan = document.createElement('span');
        nameSpan.textContent = entry.player.displayName;
        const scoreSpan = document.createElement('span');
        scoreSpan.className = 'score';
        scoreSpan.textContent = String(entry.score);
        li.appendChild(nameSpan);
        li.appendChild(scoreSpan);
        leaderboardList.appendChild(li);
      });
    }
  } catch (err) {
    console.error('Failed to fetch leaderboard:', err);
  }
}

function playNotificationSound() {
  // Simple beep using Web Audio API
  try {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';
    gain.gain.value = 0.3;
    
    oscillator.start();
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    oscillator.stop(ctx.currentTime + 0.5);
  } catch {
    // Audio not supported
  }
}

// Twitch Chat Embed
function initTwitchChatEmbed() {
  const channel = import.meta.env.VITE_TWITCH_CHANNEL;
  if (!channel) return;

  const container = document.getElementById('twitch-chat-embed');
  if (!container) return;

  const parent = window.location.hostname;
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.twitch.tv/embed/${encodeURIComponent(channel)}/chat?parent=${encodeURIComponent(parent)}&darkpopout`;
  iframe.width = '100%';
  iframe.height = '400';
  iframe.setAttribute('frameborder', '0');
  iframe.loading = 'lazy';
  container.appendChild(iframe);
}

// Start
init();
